import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useState } from "react";
import { object, string } from "yup";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldError, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

import { PredictiveAddressInput } from "@/components/organisations/pav-address-input-v2";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipButton } from "components/permissions/permissions";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { Organisation } from "data/models";
import { editOrganisation } from "data/mutations-common";
import { resolveIdentityId } from "utils/identity";

const validationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
  phoneNumber: string()
    .matches(
      /^\d+$/,
      "Please provide a valid Australian phone number without the country code",
    )
    .min(
      10,
      "Please provide a valid Australian phone number without the country code",
    )
    .max(
      10,
      "Please provide a valid Australian phone number without the country code",
    )
    .nullable(true),
  address: string().nullable(true),
});

interface ContactDetailsCardProps {
  organisation: Organisation;
}

export const ContactDetailsCard = ({
  organisation,
}: ContactDetailsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();

  const organisationMutation = useMutation({
    mutationFn: (org: Organisation) => editOrganisation(org),
    onSuccess: (updatedOrganisation) => {
      queryClient.setQueryData(
        [QueryKeys.organisation, identityId],
        updatedOrganisation,
      );
      setIsEditing(false);
    },
  });

  return (
    <Formik<Organisation>
      enableReinitialize={true}
      initialValues={{ ...organisation }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        organisationMutation.mutate({ ...values, id: organisation.id });
      }}
    >
      {({
        values,
        handleChange,
        setFieldValue,
        handleBlur,
        errors,
        touched,
        resetForm,
        isValid,
        dirty,
      }: any) => (
        <Form>
          <Card>
            <CardHeader>
              <CardTitle>Contact Details</CardTitle>
            </CardHeader>
            <CardContent>
              <FieldSet>
                <Field>
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email ?? ""}
                    disabled={!isEditing}
                    aria-invalid={touched.email && Boolean(errors.email)}
                  />
                  {touched.email && errors.email && (
                    <FieldError>{errors.email}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor="phoneNumber">Phone</FieldLabel>
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phoneNumber ?? ""}
                    disabled={!isEditing}
                    aria-invalid={
                      touched.phoneNumber && Boolean(errors.phoneNumber)
                    }
                  />
                  {touched.phoneNumber && errors.phoneNumber && (
                    <FieldError>{errors.phoneNumber}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor="address">Address</FieldLabel>
                  <PredictiveAddressInput
                    id="address"
                    name="address"
                    value={values.address ?? ""}
                    disabled={!isEditing}
                    readOnly={!isEditing}
                    error={
                      touched.address !== null &&
                      touched.address &&
                      Boolean(errors.address)
                    }
                    helperText={touched.address && errors.address}
                    onBlur={handleBlur}
                    onChange={(val) => setFieldValue("address", val)}
                    onAddressSelect={(address, addressId) => {
                      setFieldValue("address", address);
                      setFieldValue("addressId", addressId);
                    }}
                    onClear={() => {
                      setFieldValue("address", "");
                      setFieldValue("addressId", "");
                    }}
                  />
                </Field>
              </FieldSet>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              {!isEditing && (
                <PermissionTooltipButton
                  type="button"
                  permissionKeys={[PermissionsKeys.orgInfoUpdate]}
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </PermissionTooltipButton>
              )}
              {isEditing && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={organisationMutation.isPending}
                    onClick={() => {
                      resetForm();
                      setIsEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      !isValid || !dirty || organisationMutation.isPending
                    }
                  >
                    {organisationMutation.isPending ? (
                      <>
                        <Spinner />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        </Form>
      )}
    </Formik>
  );
};
